import React, { useEffect } from 'react';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import DefaultSkeletonSymbolLoader from '@/components/skeleton/symbol/DefaultSkeletonSymbolLoader';
import { Button } from '@/components/ui/button';
import ArrowRightSLineIcon from 'remixicon-react/ArrowRightSLineIcon';
import ArrowLeftSLineIcon from 'remixicon-react/ArrowLeftSLineIcon';

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    currentPage: number;
    totalPages: number;
    handlePageSizeChangeAction: (size: number) => void;
    pageSize: number;
    sorting: SortingState;
    setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
    handlePaginationAction?: any;
}

export function DataTable<TData, TValue>({
    columns,
    data,
    sorting,
    setSorting,
    handlePaginationAction,
    currentPage,
    totalPages,
    pageSize,
}: DataTableProps<TData, TValue>) {
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: setSorting,
        state: {
            sorting,
        },
    });

    useEffect(() => {
        table.setPageSize(pageSize);
    }, [pageSize, table]);

    return (
        <>
            <Table className={'table w-full'}>
                <TableHeader className={'table-header-group'}>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow
                            className={'!table-row !border-0 !px-2 py-1'}
                            key={headerGroup.id}
                        >
                            {headerGroup.headers.map((header, columnIndex) => (
                                <TableHead
                                    key={header.id}
                                    className={`table-cell p-0 px-2 text-center font-light whitespace-nowrap text-xs max-2xl:text-xxs ${columnIndex === 0 ? 'max-lg:sticky max-lg:right-0 max-lg:h-auto dark:border-white/10 border-l border-primary-dark/10 max-lg:z-10 bg-white dark:bg-primary-dark dark:text-white' : ''}`}
                                >
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                              header.column.columnDef.header,
                                              header.getContext(),
                                          )}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody className={'table-row-group'}>
                    {table?.getRowModel().rows.length ? (
                        table.getRowModel().rows.map((row, index) => (
                            <React.Fragment key={row.id}>
                                {index === 0 && (
                                    <tr className={'table-row'}>
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell
                                                key={cell.id}
                                                className={'block w-32 p-0'}
                                            ></TableCell>
                                        ))}
                                    </tr>
                                )}
                                <TableRow
                                    className={`!table-row border-0 !px-2 transition-all bg-transparent ${index % 2 === 0 ? 'bg-gray-300 dark:bg-gray-800' : 'dark:bg-gray-800'}`}
                                    key={row.id}
                                    data-state={row.getIsSelected() && 'selected'}
                                >
                                    {row.getVisibleCells().map((cell, cellIndex) => (
                                        <TableCell
                                            key={cell.id}
                                            className={`text-xs relative table-cell max-2xl:text-xxs whitespace-nowrap px-0 ${cellIndex === 0 ? ' max-lg:z-10 pr-2 max-lg:sticky max-lg:right-0 max-lg:h-auto dark:border-white/10 border-l border-primary-dark/10 w-32' : ''} ${index % 2 === 0 ? 'bg-gray-100 dark:bg-gray-800' : 'bg-white dark:bg-primary-active'}`}
                                        >
                                            {/*{cellIndex === 0 && (*/}
                                            {/*    <span className={'absolute top-5 max-2xl:text-xxs'}>*/}
                                            {/*        {index + 1}*/}
                                            {/*    </span>*/}
                                            {/*)}*/}
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext(),
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </React.Fragment>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={columns.length} className='h-24 text-center'>
                                <DefaultSkeletonSymbolLoader />
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            <div className='mt-5 flex items-center justify-between max-lg:px-3 lg:justify-center gap-1 pb-3 text-sm'>
                <Button
                    aria-label={'pagination_start'}
                    title={'صفحه اول'}
                    size={'sm'}
                    className='inline-flex border px-2 py-1 !text-xs max-lg:!text-[10px] text-white dark:border-white/30'
                    onClick={() => handlePaginationAction(1)}
                    disabled={currentPage === 1}
                >
                    صفحه اول
                </Button>
                <Button
                    aria-label={'pagination_next'}
                    title={'next'}
                    size='sm'
                    className={'border dark:border-white/30 '}
                    onClick={() => handlePaginationAction(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    <ArrowRightSLineIcon className={'text-white'} />
                </Button>
                <div className={'text-xs max-lg:!text-[10px]'}>
                    صفحه {currentPage} از {Math.ceil(totalPages / pageSize)}
                </div>
                <Button
                    aria-label={'pagination_prev'}
                    title={'prev'}
                    size='sm'
                    className={'border dark:border-white/30'}
                    onClick={() => handlePaginationAction(currentPage + 1)}
                    disabled={currentPage === Math.ceil(totalPages / pageSize)}
                >
                    <ArrowLeftSLineIcon className={'text-white'} />
                </Button>
                <Button
                    aria-label={'pagination_end'}
                    title={'صفحه آخر'}
                    size={'sm'}
                    className='inline-flex border px-2 py-1 !text-xs max-lg:!text-[10px] text-white dark:border-white/30'
                    onClick={() => handlePaginationAction(Math.ceil(totalPages / pageSize))}
                    disabled={currentPage === Math.ceil(totalPages / pageSize)}
                >
                    صفحه آخر
                </Button>
            </div>
        </>
    );
}
